import { StaleBrowserSessionError } from "@/lib/browser-session";

export const BRAND_TEXT_LIMIT = 500;
export const BRAND_PREFIX_LIMIT = 100;
export const BRAND_SOURCE_LIMIT = 20;

export class BrandSearchError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfter: string | null = null
  ) {
    super(message);
    this.name = "BrandSearchError";
  }
}

export function brandSearchFailure(response?: Response): BrandSearchError {
  const status = response?.status ?? 503;
  const message =
    status === 429
      ? "Brand search is busy. Please try again shortly."
      : status === 504 || status === 408
        ? "Brand search timed out. Please try again."
        : status === 400 || status === 422
          ? "Check your brand search and try again."
          : status === 401 || status === 403
            ? "Please sign in to search brands."
            : "Brand search is unavailable. Please try again.";
  return new BrandSearchError(message, status, response?.headers?.get("Retry-After") ?? null);
}

export function brandSearchErrorMessage(error: unknown): string {
  if (error instanceof BrandSearchError) return error.message;
  if (
    error instanceof StaleBrowserSessionError ||
    (typeof error === "object" &&
      error !== null &&
      "code" in error &&
      ["auth/user-disabled", "auth/user-token-expired", "auth/user-not-found"].includes(
        String(error.code)
      ))
  ) {
    return "Please sign in to search brands.";
  }
  if (isBrandSearchTimeout(error)) {
    return "Brand search timed out. Please try again.";
  }
  return "Brand search is unavailable. Please try again.";
}

export function isBrandSearchTimeout(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error.name === "TimeoutError" || error.name === "AbortError")
  );
}

export function validateBrandText(value: string, kind: "text" | "prefix"): void {
  const limit = kind === "text" ? BRAND_TEXT_LIMIT : BRAND_PREFIX_LIMIT;
  if (!value.trim()) throw new BrandSearchError("Enter a brand search.", 400);
  if (value.length > limit) {
    throw new BrandSearchError(
      `Use ${limit} characters or fewer for ${kind === "text" ? "your search" : "a brand name"}.`,
      400
    );
  }
}

export function validateSourceBrands(brands: string[]): void {
  if (!brands.length || brands.length > BRAND_SOURCE_LIMIT || brands.some((b) => !b.trim())) {
    throw new BrandSearchError(`Choose between 1 and ${BRAND_SOURCE_LIMIT} brands.`, 400);
  }
}
